<template>
	<Teleport to="body">
		<div
			v-if="isVisible"
			class="pwa-install-guide-overlay"
			@click.self="handleClose"
		>
			<div
				class="pwa-install-guide"
				role="dialog"
				aria-modal="true"
				:aria-labelledby="'pwa-guide-title'"
			>
				<button
					class="pwa-install-guide__close"
					type="button"
					:aria-label="closeLabel"
					@click="handleClose"
				>
					<i class="fas fa-times" aria-hidden="true"></i>
				</button>

				<h2 id="pwa-guide-title" class="pwa-install-guide__title">
					{{ title }}
				</h2>

				<!-- Already installed message (takes priority - mutually exclusive with install options) -->
				<div v-if="isAlreadyInstalled" class="pwa-install-guide__installed">
					<i class="fas fa-check-circle" aria-hidden="true"></i>
					<p>{{ alreadyInstalledText }}</p>
				</div>

				<!-- iOS Instructions -->
				<div v-else-if="isIos" class="pwa-install-guide__steps">
					<div class="pwa-install-guide__step">
						<span class="pwa-install-guide__step-number">1</span>
						<div class="pwa-install-guide__step-content">
							<p>{{ iosStep1 }}</p>
							<div class="pwa-install-guide__icon-demo">
								<i class="fas fa-share-square" aria-hidden="true"></i>
							</div>
						</div>
					</div>
					<div class="pwa-install-guide__step">
						<span class="pwa-install-guide__step-number">2</span>
						<div class="pwa-install-guide__step-content">
							<p>{{ iosStep2 }}</p>
							<div class="pwa-install-guide__icon-demo">
								<i class="fas fa-plus-square" aria-hidden="true"></i>
								<span>{{ addToHomeScreenText }}</span>
							</div>
						</div>
					</div>
					<div class="pwa-install-guide__step">
						<span class="pwa-install-guide__step-number">3</span>
						<div class="pwa-install-guide__step-content">
							<p>{{ iosStep3 }}</p>
						</div>
					</div>
				</div>

				<!-- Android Instructions (when no native prompt available) -->
				<div v-else-if="!canUseNativePrompt" class="pwa-install-guide__steps">
					<div class="pwa-install-guide__step">
						<span class="pwa-install-guide__step-number">1</span>
						<div class="pwa-install-guide__step-content">
							<p>{{ androidStep1 }}</p>
							<div class="pwa-install-guide__icon-demo">
								<i class="fas fa-ellipsis-v" aria-hidden="true"></i>
							</div>
						</div>
					</div>
					<div class="pwa-install-guide__step">
						<span class="pwa-install-guide__step-number">2</span>
						<div class="pwa-install-guide__step-content">
							<p>{{ androidStep2 }}</p>
							<div class="pwa-install-guide__icon-demo">
								<i class="fas fa-mobile-alt" aria-hidden="true"></i>
								<span>{{ installAppText }}</span>
							</div>
						</div>
					</div>
					<div class="pwa-install-guide__step">
						<span class="pwa-install-guide__step-number">3</span>
						<div class="pwa-install-guide__step-content">
							<p>{{ androidStep3 }}</p>
						</div>
					</div>
				</div>

				<!-- Android with native prompt available -->
				<div v-else class="pwa-install-guide__native">
					<p class="pwa-install-guide__description">{{ nativePromptDescription }}</p>
					<button
						class="pwa-install-guide__install-btn"
						type="button"
						@click="handleNativeInstall"
						:disabled="isInstalling"
					>
						<i class="fas fa-download" aria-hidden="true"></i>
						{{ isInstalling ? installingText : installButtonText }}
					</button>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script lang="ts" setup>
	import { ref, computed } from 'vue';
	import LOCALIZATION from '@core/classes/Localization';
	import util from '@core/services/util';

	interface IPwaInstallGuideProps {
		isVisible: boolean;
		language?: 'English' | 'Spanish';
		isInstallable?: boolean;
		isInstalled?: boolean;
		/** Async callback for native install prompt. Returns outcome. Use emit('close') for close events. */
		onInstall?: () => Promise<'accepted' | 'dismissed' | 'unavailable' | 'ios' | 'already_installed'>;
	}

	const props = withDefaults(defineProps<IPwaInstallGuideProps>(), {
		language: 'English',
		isInstallable: false,
		isInstalled: false,
	});

	const emit = defineEmits<{
		(e: 'close'): void;
		(e: 'installed'): void;
	}>();

	const isInstalling = ref(false);
	const isIos = computed(() => util.isIosDevice());
	const canUseNativePrompt = computed(() => props.isInstallable && !isIos.value);
	const isAlreadyInstalled = computed(() => props.isInstalled || util.isStandaloneApp());

	// Localization
	const loc = LOCALIZATION.customerPortalNavBar.pwaInstallGuide;

	const title = computed(() => loc?.title?.[props.language] ?? 'Add to Home Screen');
	const closeLabel = computed(() => loc?.closeLabel?.[props.language] ?? 'Close');

	// iOS steps
	const iosStep1 = computed(() => loc?.iosStep1?.[props.language] ?? 'Tap the Share button at the bottom of Safari');
	const iosStep2 = computed(() => loc?.iosStep2?.[props.language] ?? 'Scroll down and tap "Add to Home Screen"');
	const iosStep3 = computed(() => loc?.iosStep3?.[props.language] ?? 'Tap "Add" to confirm');
	const addToHomeScreenText = computed(() => loc?.addToHomeScreen?.[props.language] ?? 'Add to Home Screen');

	// Android steps (manual)
	const androidStep1 = computed(() => loc?.androidStep1?.[props.language] ?? 'Tap the menu icon (three dots) in your browser');
	const androidStep2 = computed(() => loc?.androidStep2?.[props.language] ?? 'Tap "Install app" or "Add to Home screen"');
	const androidStep3 = computed(() => loc?.androidStep3?.[props.language] ?? 'Tap "Install" to confirm');
	const installAppText = computed(() => loc?.installApp?.[props.language] ?? 'Install app');

	// Native prompt
	const nativePromptDescription = computed(() => loc?.nativePromptDescription?.[props.language] ?? 'Add the Saturn portal to your home screen for quick access.');
	const installButtonText = computed(() => loc?.installButton?.[props.language] ?? 'Install Now');
	const installingText = computed(() => loc?.installing?.[props.language] ?? 'Installing...');

	// Already installed
	const alreadyInstalledText = computed(() => loc?.alreadyInstalled?.[props.language] ?? 'This app is already installed on your device.');

	const handleClose = () => {
		emit('close');
	};

	const handleNativeInstall = async () => {
		if (!props.onInstall || isInstalling.value) return;

		isInstalling.value = true;
		try {
			const result = await props.onInstall();
			if (result === 'accepted') {
				emit('installed');
			}
		} catch (error) {
			// Log error but don't crash - user can retry or close modal
			console.error('PWA install failed:', error);
		} finally {
			isInstalling.value = false;
		}
	};
</script>

<style scoped>
	.pwa-install-guide-overlay {
		position: fixed;
		inset: 0;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		align-items: flex-end;
		justify-content: center;
		z-index: 10000;
		padding: 0;
	}

	.pwa-install-guide {
		background-color: var(--customer-portal-white-color, #fff);
		border-radius: 16px 16px 0 0;
		padding: 24px;
		width: 100%;
		max-width: 100%;
		max-height: 85vh;
		overflow-y: auto;
		position: relative;
		animation: slideUp 0.3s ease-out;
	}

	@keyframes slideUp {
		from {
			transform: translateY(100%);
		}
		to {
			transform: translateY(0);
		}
	}

	.pwa-install-guide__close {
		position: absolute;
		top: 16px;
		right: 16px;
		background: none;
		border: none;
		font-size: 20px;
		color: var(--customer-portal-secondary-text-color, #666);
		cursor: pointer;
		padding: 8px;
		line-height: 1;
	}

	.pwa-install-guide__close:hover {
		color: var(--customer-portal-secondary-color, #333);
	}

	.pwa-install-guide__title {
		font-size: 20px;
		font-weight: 600;
		color: var(--customer-portal-secondary-color, #333);
		margin: 0 0 24px 0;
		padding-right: 40px;
	}

	.pwa-install-guide__steps {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	.pwa-install-guide__step {
		display: flex;
		gap: 16px;
		align-items: flex-start;
	}

	.pwa-install-guide__step-number {
		flex-shrink: 0;
		width: 32px;
		height: 32px;
		border-radius: 50%;
		background-color: var(--customer-portal-primary-color, #0E2C4F);
		color: #fff;
		display: flex;
		align-items: center;
		justify-content: center;
		font-weight: 600;
		font-size: 14px;
	}

	.pwa-install-guide__step-content {
		flex: 1;
	}

	.pwa-install-guide__step-content p {
		margin: 0 0 12px 0;
		color: var(--customer-portal-secondary-color, #333);
		font-size: 15px;
		line-height: 1.4;
	}

	.pwa-install-guide__icon-demo {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		background-color: var(--customer-portal-white-background-color, #f4f4f4);
		padding: 8px 12px;
		border-radius: 8px;
		font-size: 14px;
		color: var(--customer-portal-secondary-text-color, #666);
	}

	.pwa-install-guide__icon-demo i {
		font-size: 18px;
		color: var(--customer-portal-primary-color, #0E2C4F);
	}

	.pwa-install-guide__native {
		text-align: center;
	}

	.pwa-install-guide__description {
		color: var(--customer-portal-secondary-text-color, #666);
		font-size: 15px;
		line-height: 1.5;
		margin: 0 0 24px 0;
	}

	.pwa-install-guide__install-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		background-color: var(--customer-portal-primary-color, #0E2C4F);
		color: #fff;
		border: none;
		border-radius: 8px;
		padding: 14px 32px;
		font-size: 16px;
		font-weight: 600;
		cursor: pointer;
		transition: background-color 0.2s;
		width: 100%;
	}

	.pwa-install-guide__install-btn:hover:not(:disabled) {
		background-color: var(--customer-portal-primary-hover-color, #163d6b);
	}

	.pwa-install-guide__install-btn:disabled {
		opacity: 0.7;
		cursor: not-allowed;
	}

	.pwa-install-guide__installed {
		display: flex;
		align-items: center;
		gap: 12px;
		background-color: #e8f5e9;
		padding: 16px;
		border-radius: 8px;
	}

	.pwa-install-guide__installed i {
		font-size: 24px;
		color: #4caf50;
	}

	.pwa-install-guide__installed p {
		margin: 0;
		color: #2e7d32;
		font-size: 14px;
	}

	/* Tablet and up */
	@media (min-width: 48em) {
		.pwa-install-guide-overlay {
			align-items: center;
			padding: 20px;
		}

		.pwa-install-guide {
			border-radius: 16px;
			max-width: 420px;
		}
	}
</style>
